<template>
  <div class="p-4 content-container">
    <div>
      <div class="slider-control">
        <input
          @change="minValueRangeInputChange"
          type="range"
          class="min-slider"
          :ref="`${this.id}-fromSlider`"
          :aria-label="OADALabels.minLabel"
          :min="rangeMin"
          :max="rangeMax"
          step="1"
          v-model.number="minValueRangeInput"
        />
        <input
          @change="maxValueRangeInputChange"
          type="range"
          class="max-slider"
          :ref="`${this.id}-toSlider`"
          :aria-label="OADALabels.maxLabel"
          :min="rangeMin"
          :max="rangeMax"
          step="1"
          v-model.number="maxValueRangeInput"
        />
      </div>
      <div>
        <div class="row">
          <div class="col">
            <p class="float-left">{{ minValueLabel }}</p>
            <p class="float-right">{{ maxValueLabel }}</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="slider-input-container text-center">
            <mobialis-text-input-with-validation
              :vid="`${this.id}-minTextInput`"
              :ref="`${this.id}-minTextInput`"
              @keyup.enter.native="validateTextInputMin"
              inputmode="numeric"
              class="slider-input"
              v-model.number="minValueTextInput"
              :mask-type="maskType"
              :label="OADALabels.minLabel"
              :hiddenLabel="true"
            ></mobialis-text-input-with-validation>
            <div class="px-1"><hr /></div>
            <mobialis-text-input-with-validation
              :vid="`${this.id}-maxTextInput`"
              :ref="`${this.id}-maxTextInput`"
              @keyup.enter.native="validateTextInputMax"
              inputmode="numeric"
              class="slider-input"
              v-model.number="maxValueTextInput"
              :mask-type="maskType"
              :label="OADALabels.maxLabel"
              :hiddenLabel="true"
            ></mobialis-text-input-with-validation>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'Slider',
  data() {
    return {
      minValue: '',
      maxValue: '',
      minValueTextInput: '',
      maxValueTextInput: '',
      minValueRangeInput: '',
      maxValueRangeInput: '',
    };
  },
  props: {
    id: {
      type: String,
      default: 'slider',
    },
    title: {
      type: String,
      default: '',
    },
    rangeMin: {
      type: Number,
      default: 0,
    },
    rangeMax: {
      type: Number,
      default: 250000,
    },
    initialMin: {
      type: Number,
      default: null,
    },
    initialMax: {
      type: Number,
      default: null,
    },
    valueLabel: {
      type: String,
      required: false,
      default: '',
    },
    valueType: {
      type: String,
      required: false,
      default: 'number',
      validator: valueType => ['mileage', 'price', 'number'].indexOf(valueType) > -1,
    },
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
    }),
    minValueLabel() {
      return this.setValueLabels(this.rangeMin);
    },
    maxValueLabel() {
      return this.setValueLabels(this.rangeMax);
    },
    OADALabels() {
      return {
        minLabel: `${this.$t('message.credit.sliderCard.min')} ${this.title}`,
        maxLabel: `${this.$t('message.credit.sliderCard.max')} ${this.title}`,
      };
    },
    fromSlider() {
      return this.$refs[`${this.id}-fromSlider`];
    },
    toSlider() {
      return this.$refs[`${this.id}-toSlider`];
    },
    maskType() {
      switch (this.valueType) {
        case 'mileage':
          return this.languageCode === 'fr-CA' ? 'frenchInt' : 'int';
        case 'price':
          return this.languageCode === 'fr-CA' ? 'frenchInt' : 'moneyInt';
        case 'number':
          return 'int';
        default:
          return false;
      }
    },
  },
  watch: {
    initialMin(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.minValue = newValue;
        this.minValueRangeInput = newValue;
        this.minValueTextInput = newValue;
        this.updateSliderStyling();
      }
    },
    initialMax(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.maxValue = newValue;
        this.maxValueRangeInput = newValue;
        this.maxValueTextInput = newValue;
        this.updateSliderStyling();
      }
    },
  },
  methods: {
    minValueRangeInputChange() {
      // make sure the min value is not greater than the max value
      if (this.minValueRangeInput > this.maxValue) {
        this.minValueRangeInput = this.minValue;
      } else {
        this.minValueTextInput = this.minValueRangeInput;
        this.minValue = this.minValueRangeInput;
        this.emitValues();
      }
    },
    maxValueRangeInputChange() {
      this.setToggleAccessible(this.toSlider);
      // make sure the max value is not less than the min value
      if (this.maxValueRangeInput < this.minValue) {
        this.maxValueRangeInput = this.maxValue;
      } else {
        this.maxValueTextInput = this.maxValueRangeInput;
        this.maxValue = this.maxValueRangeInput;
        this.emitValues();
      }
    },
    validateTextInputMin() {
      // reset the value if it is not valid
      if (
        (!this.minValueTextInput && this.minValueTextInput !== 0) ||
        this.minValueTextInput > this.maxValue ||
        this.minValueTextInput < this.rangeMin
      ) {
        // reset if empty
        this.minValueTextInput = this.minValue;
      } else {
        this.minValue = this.minValueTextInput;
        this.minValueRangeInput = this.minValueTextInput;
        this.emitValues();
      }
    },
    validateTextInputMax() {
      // reset the value if it is not valid
      if (!this.maxValueTextInput || this.maxValueTextInput < this.minValue || this.maxValueTextInput > this.rangeMax) {
        this.maxValueTextInput = this.maxValue;
      } else {
        this.maxValue = this.maxValueTextInput;
        this.maxValueRangeInput = this.maxValueTextInput;
        this.emitValues();
      }
    },
    setValueLabels(value) {
      switch (this.valueType) {
        case 'mileage':
          return `${this.$n(value, 'number', this.languageCode)} ${this.$t('message.credit.inventoryMatching.km')}`;
        case 'price':
          return this.$n(value, 'currencyDollars', this.languageCode);
        default:
          return this.$n(value, 'number', this.languageCode) + this.valueLabel;
      }
    },
    updateSliderStyling() {
      this.fromSlider.value = this.minValue;
      this.toSlider.value = this.maxValue;
      this.fillSlider(this.minValue, this.maxValue, '#C6C6C6', '#25daa5', this.toSlider);
      this.fillSlider(this.fromSlider, this.toSlider, '#C6C6C6', 'var(--primary)', this.toSlider);
    },
    emitValues() {
      this.$emit('input', {
        selectedMinValue: this.minValue,
        selectedMaxValue: this.maxValue,
      });
      this.updateSliderStyling();
    },
    fillSlider(from, to, sliderColor, rangeColor, controlSlider) {
      const rangeDistance = to.max - to.min;
      const fromPosition = from.value - to.min;
      const toPosition = to.value - to.min;
      // eslint-disable-next-line no-param-reassign
      controlSlider.style.background = `linear-gradient(
        to right,
        ${sliderColor} 0%,
        ${sliderColor} ${(fromPosition / rangeDistance) * 100}%,
        ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
        ${rangeColor} ${(toPosition / rangeDistance) * 100}%,
        ${sliderColor} ${(toPosition / rangeDistance) * 100}%,
        ${sliderColor} 100%)`;
    },
    setToggleAccessible() {
      if (this.maxValue <= this.minValue) {
        this.toSlider.style.zIndex = 2;
      } else {
        this.toSlider.style.zIndex = 0;
      }
    },
  },
  mounted() {
    this.minValue = this.initialMin ? this.initialMin : this.rangeMin;
    this.maxValue = this.initialMax ? this.initialMax : this.rangeMax;
    this.minValueTextInput = this.minValue;
    this.maxValueTextInput = this.maxValue;
    this.minValueRangeInput = this.minValue;
    this.maxValueRangeInput = this.maxValue;
    this.emitValues();
    this.setToggleAccessible(this.toSlider);

    const minTextInput = this.$refs[`${this.id}-minTextInput`].$el.querySelector('input');
    if (minTextInput) {
      minTextInput.addEventListener('blur', () => {
        this.validateTextInputMin();
      });
    }
    const maxTextInput = this.$refs[`${this.id}-maxTextInput`].$el.querySelector('input');
    if (maxTextInput) {
      maxTextInput.addEventListener('blur', () => {
        this.validateTextInputMax();
      });
    }
  },
  beforeDestroy() {
    const minTextInput = this.$refs[`${this.id}-minTextInput`].$el.querySelector('input');
    if (minTextInput) {
      minTextInput.removeEventListener('blur', () => {
        this.validateTextInputMin();
      });
    }
    const maxTextInput = this.$refs[`${this.id}-maxTextInput`].$el.querySelector('input');
    if (maxTextInput) {
      maxTextInput.removeEventListener('blur', () => {
        this.validateTextInputMax();
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.content-container {
  display: flex;
  flex-direction: column;
}

.slider-control {
  position: relative;
  width: 100%;
  border-radius: 25px;
  background: var(--primary);
}

input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  pointer-events: all;
  height: 12px;
  width: 12px;
  background: #ffffff;
  border-radius: 50%;
  border: 1px solid var(--primary);
  cursor: pointer;
}

input[type='range']::-moz-range-thumb {
  -webkit-appearance: none;
  pointer-events: all;
  height: 12px;
  width: 12px;
  background: #ffffff;
  border-radius: 50%;
  border: 1px solid var(--primary);
  cursor: pointer;
}

input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  height: 2px;
  width: 100%;
  position: absolute;
  background-color: #c6c6c6;
  pointer-events: none;
  margin-top: -3px;
}

input[type='range'].min-slider {
  height: 0;
  z-index: 1;
  margin-top: 0;
}

input[type='range'].max-slider {
  height: 6px;
  border-radius: 25px;
}

.slider-input-container {
  div {
    width: 16px;
    display: inline-block;

    hr {
      height: 5px;
      margin: 0;
    }
  }

  .slider-input {
    height: 48px;
    width: 45%;
    border-radius: 4px;
    -moz-appearance: textfield;
  }

  .slider-input::v-deep {
    input {
      text-align: center;
      color: #6e7681;
    }
    label {
      display: none;
    }
  }
}
</style>
